import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Swords, Loader2, Copy, Trophy, Zap, Target, Cloud, Megaphone, Bot } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const TRACKS = [
  { id: "data_cloud", label: "Data Cloud", icon: Cloud, accent: "indigo" },
  { id: "sfmc", label: "SFMC", icon: Megaphone, accent: "teal" },
  { id: "mc_next", label: "MC Next", icon: Bot, accent: "fuchsia" },
];

const TRACK_ACTIVE_CLASSES = {
  teal: "bg-teal-500/20 text-teal-300 border border-teal-400/30",
  fuchsia: "bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/30",
  indigo: "bg-indigo-500/20 text-indigo-300 border border-indigo-400/30",
};

export default function DuelPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbPeriod, setLbPeriod] = useState("all");
  const [lbScope, setLbScope] = useState("all");
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("play");
  const [track, setTrack] = useState("data_cloud");
  const [matching, setMatching] = useState(false);
  const [inviteRoom, setInviteRoom] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/duel/stats/me").then((r) => setStats(r.data));
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (tab === "leaderboard") api.get(`/duel/leaderboard?period=${lbPeriod}&scope=${lbScope}`).then((r) => setLeaderboard(r.data));
    if (tab === "history") api.get("/duel/history").then((r) => setHistory(r.data));
  }, [tab, lbPeriod, lbScope]);

  const startQuickMatch = async () => {
    setMatching(true);
    try {
      const { data } = await api.post("/duel/quick-match/join", { track });
      if (data.matched) { navigate(`/duel/room/${data.room_id}`); return; }
      pollRef.current = setInterval(async () => {
        const { data: status } = await api.get("/duel/quick-match/status");
        if (status.matched) { clearInterval(pollRef.current); navigate(`/duel/room/${status.room_id}`); }
      }, 1500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start matchmaking");
      setMatching(false);
    }
  };

  const cancelMatch = async () => {
    clearInterval(pollRef.current);
    await api.post("/duel/quick-match/cancel");
    setMatching(false);
  };

  const createInvite = async () => {
    try {
      const { data } = await api.post("/duel/invite/create", { track });
      setInviteRoom(data);
      pollRef.current = setInterval(async () => {
        const { data: room } = await api.get(`/duel/room/${data.room_id}`);
        if (room.player_ids.length >= 2) { clearInterval(pollRef.current); navigate(`/duel/room/${data.room_id}`); }
      }, 2000);
    } catch (e) {
      toast.error("Could not create invite room");
    }
  };

  const joinWithCode = async () => {
    if (!joinCode.trim()) return;
    setJoining(true);
    try {
      const { data } = await api.post("/duel/invite/join", { code: joinCode.trim() });
      navigate(`/duel/room/${data.room_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid or expired code");
    } finally {
      setJoining(false);
    }
  };

  const copyInviteLink = () => {
    if (!inviteRoom) return;
    navigator.clipboard.writeText(`${window.location.origin}/duel/join/${inviteRoom.code}`)
      .then(() => toast.success("Invite link copied!")).catch(() => toast.error(`Copy manually: ${inviteRoom.code}`));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="duel-page">
      <div className="flex items-center gap-3 mb-2">
        <Swords className="w-6 h-6 text-rose-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-rose-400">Live 1-on-1</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">Duel Mode</h1>
      <p className="text-sm text-slate-400 mt-2 max-w-2xl">Challenge another learner to a real-time knowledge duel, on either track. AI generates fresh questions every match.</p>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          {[
            { label: "Rating", value: stats.rating, icon: Zap },
            { label: "Record", value: `${stats.wins}W-${stats.losses}L-${stats.draws}D`, icon: Trophy },
            { label: "Win Rate", value: `${stats.win_rate}%`, icon: Target },
            { label: "Streak", value: stats.current_streak, icon: Swords },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="glass-panel rounded-xl p-3 text-center" data-testid={`duel-stat-${s.label.toLowerCase()}`}>
                <Icon className="w-4 h-4 text-rose-300 mx-auto mb-1" />
                <p className="text-lg font-bold text-white">{s.value}</p>
                <p className="text-[10px] text-slate-400 uppercase">{s.label}</p>
              </div>
            );
          })}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList data-testid="duel-tabs">
          <TabsTrigger value="play" data-testid="duel-tab-play">Play</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="duel-tab-leaderboard">Leaderboard</TabsTrigger>
          <TabsTrigger value="history" data-testid="duel-tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="play" className="mt-6 space-y-6">
          <div className="flex items-center gap-2" data-testid="duel-track-selector">
            <span className="text-xs font-semibold text-slate-400 mr-1">Track:</span>
            {TRACKS.map((t) => {
              const Icon = t.icon;
              const active = track === t.id;
              return (
                <button
                  key={t.id} onClick={() => setTrack(t.id)} disabled={matching || !!inviteRoom}
                  data-testid={`duel-track-${t.id}`}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors disabled:opacity-50 ${
                    active ? TRACK_ACTIVE_CLASSES[t.accent] : "bg-white/5 text-slate-400 hover:text-white border border-transparent"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </div>

          <div className="glass-panel rounded-2xl p-6">
            <h3 className="font-heading font-semibold text-white mb-1">Quick Match</h3>
            <p className="text-sm text-slate-400 mb-4">Get matched instantly with a similarly-rated learner who's online now, on the {TRACKS.find((t) => t.id === track)?.label} track.</p>
            {matching ? (
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
                <span className="text-sm text-slate-300" data-testid="duel-matching-status">Finding an opponent...</span>
                <button onClick={cancelMatch} data-testid="duel-cancel-match-btn" className="ml-auto text-xs text-slate-400 hover:text-red-400">Cancel</button>
              </div>
            ) : (
              <button onClick={startQuickMatch} data-testid="duel-quick-match-btn" className="flex items-center gap-2 bg-rose-500 hover:bg-rose-500/90 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
                <Swords className="w-4 h-4" /> Find Duel
              </button>
            )}
          </div>

          <div className="glass-panel rounded-2xl p-6">
            <h3 className="font-heading font-semibold text-white mb-1">Invite a Friend</h3>
            <p className="text-sm text-slate-400 mb-4">Create a private room and share the code, or join a friend's room.</p>
            {inviteRoom ? (
              <div className="flex items-center gap-3 flex-wrap" data-testid="duel-invite-code-display">
                <span className="font-heading text-2xl font-bold text-indigo-300 tracking-widest">{inviteRoom.code}</span>
                <button onClick={copyInviteLink} data-testid="duel-copy-invite-btn" className="flex items-center gap-1.5 text-xs font-semibold bg-white/10 hover:bg-white/15 px-3 py-1.5 rounded-lg text-white"><Copy className="w-3 h-3" /> Copy Link</button>
                <span className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Waiting for friend to join...</span>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={createInvite} data-testid="duel-create-invite-btn" className="text-sm font-semibold bg-white/10 hover:bg-white/15 text-white px-4 py-2 rounded-lg">Create Room</button>
                <span className="text-slate-400 text-xs">or</span>
                <input
                  value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="Enter code" data-testid="duel-join-code-input"
                  className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white w-32 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                />
                <button onClick={joinWithCode} disabled={joining} data-testid="duel-join-code-btn" className="text-sm font-semibold bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg disabled:opacity-60">Join</button>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-6">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {[["all", "All-Time"], ["weekly", "This Week"], ["monthly", "This Month"]].map(([v, label]) => (
              <button key={v} onClick={() => setLbPeriod(v)} data-testid={`duel-lb-period-${v}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${lbPeriod === v ? "bg-indigo-500/20 text-indigo-300 border border-indigo-400/30" : "bg-white/5 text-slate-400 hover:text-white border border-transparent"}`}>
                {label}
              </button>
            ))}
            <span className="text-slate-400 mx-1">|</span>
            {[["all", "Everyone"], ["friends", "Friends Only"]].map(([v, label]) => (
              <button key={v} onClick={() => setLbScope(v)} data-testid={`duel-lb-scope-${v}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${lbScope === v ? "bg-purple-500/20 text-purple-300 border border-purple-400/30" : "bg-white/5 text-slate-400 hover:text-white border border-transparent"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="glass-panel rounded-2xl divide-y divide-white/5" data-testid="duel-leaderboard-list">
            {leaderboard.length === 0 && <p className="p-6 text-sm text-slate-400 text-center">No ranked duels {lbScope === "friends" ? "among your friends " : ""}in this period yet.</p>}
            {leaderboard.map((row) => (
              <div key={row.user_id} className="flex items-center gap-3 p-3.5" data-testid={`duel-leaderboard-row-${row.rank}`}>
                <span className="w-7 text-sm font-bold text-slate-400">#{row.rank}</span>
                <span className="flex-1 text-sm text-white truncate">{row.name}</span>
                <span className="text-xs text-slate-400">{row.wins}W-{row.losses}L</span>
                <span className={`text-sm font-bold w-16 text-right ${lbPeriod === "all" ? "text-indigo-300" : row.rating >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {lbPeriod !== "all" && row.rating >= 0 ? "+" : ""}{row.rating}
                </span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <div className="glass-panel rounded-2xl divide-y divide-white/5" data-testid="duel-history-list">
            {history.length === 0 && <p className="p-6 text-sm text-slate-400 text-center">No duels played yet.</p>}
            {history.map((room) => {
              const opponentId = room.player_ids.find((p) => p !== user?.user_id);
              const opponentName = room.player_names?.[opponentId] || "Opponent";
              const won = !room.is_draw && room.winner_id === user?.user_id;
              const outcome = room.is_draw ? "Draw" : won ? "Won" : "Lost";
              const myScore = room.scores?.[user?.user_id]?.points ?? 0;
              const oppScore = room.scores?.[opponentId]?.points ?? 0;
              return (
                <div key={room.room_id} className="flex items-center gap-3 p-3.5" data-testid={`duel-history-row-${room.room_id}`}>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${won ? "bg-emerald-400/15 text-emerald-400" : room.is_draw ? "bg-slate-400/15 text-slate-400" : "bg-red-400/15 text-red-400"}`}>{outcome}</span>
                  <span className="flex-1 text-sm text-white truncate">vs {opponentName}</span>
                  <span className="text-xs text-slate-400">{myScore} - {oppScore}</span>
                  <span className={`text-xs font-bold ${(room.rating_changes?.[user?.user_id] ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {(room.rating_changes?.[user?.user_id] ?? 0) >= 0 ? "+" : ""}{room.rating_changes?.[user?.user_id] ?? 0}
                  </span>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
